import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import type { IssueStatus } from "@/domains/pm/types";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { visibleStatuses } from "@/domains/pm/statuses/statusCatalogue";
import { StatusBadge } from "./StatusBadge";

interface StatusMultiSelectListProps {
  selectedStatuses: IssueStatus[];
  onChange: (next: IssueStatus[]) => void;
  /**
   * Statuses that cannot be picked right now. The Overdue filter pins status
   * to in_progress (an overdue issue is by definition still in progress), so
   * the other two are shown disabled rather than hidden — a row that vanishes
   * reads as a bug, where a greyed one explains itself alongside `note`.
   *
   * A status the user had ALREADY ticked before Overdue was switched on stays
   * greyed but interactive: locking a checked box would be a dead end, since
   * the only way to untick it would be to turn Overdue off first.
   */
  disabledStatuses?: IssueStatus[];
  /** Explains why some rows are disabled. Rendered under the list. */
  note?: string;
  /** Scope-wide totals per status, independent of the current selection. */
  counts?: Record<IssueStatus, number>;
  /** The project in scope, or null for "All projects". Decides which custom
   *  statuses are listed. */
  buildingProjectId?: number | null;
  /** Rendered under the list. The filter popover puts "New status" here.
   *  A slot rather than the button itself, so this stays presentational —
   *  it is shared, and reaching for permissions or stores from here would
   *  make it unrenderable outside a full provider tree. */
  footer?: ReactNode;
  className?: string;
}

/**
 * Checkbox status checklist for the issue filter control.
 *
 * Deliberately not `StatusPicker`: that is a single-select that commits and
 * closes, which is the right shape for setting an issue's status and the wrong
 * one for filtering by several at once. This mirrors DisciplineMultiSelectList
 * so the two dimensions read identically in the filter popover.
 *
 * Rows follow the catalogue order — the built-ins in workflow order (in
 * progress → resolved → closed, as the Kanban columns and the old status tabs
 * both used), then the project's own statuses. Not alphabetical, which would
 * put Closed first.
 */
export function StatusMultiSelectList({
  selectedStatuses,
  onChange,
  disabledStatuses = [],
  note,
  counts,
  buildingProjectId,
  footer,
  className,
}: StatusMultiSelectListProps) {
  const { catalogue } = useStatusCatalogue();
  const options = visibleStatuses(catalogue, buildingProjectId);

  const toggle = (s: IssueStatus) =>
    onChange(
      selectedStatuses.includes(s)
        ? selectedStatuses.filter((x) => x !== s)
        : [...selectedStatuses, s]
    );

  return (
    <div className={cn("space-y-1", className)}>
      {options.map(({ key: s }) => {
        const checked = selectedStatuses.includes(s);
        const pinnedOut = disabledStatuses.includes(s);
        // Greyed either way — the pinning is what the dimming says. Only an
        // UNCHECKED pinned-out row is locked; a checked one keeps its
        // handler so the selection can still be cleared.
        const locked = pinnedOut && !checked;
        return (
          <label
            key={s}
            className={cn(
              "flex items-center gap-2 rounded px-1.5 py-1 text-xs",
              locked ? "cursor-not-allowed" : "cursor-pointer hover:bg-muted",
              pinnedOut && "opacity-50"
            )}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(s)}
              disabled={locked}
              className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 accent-primary"
            />
            <StatusBadge status={s} buildingProjectId={buildingProjectId} />
            {counts != null && (
              <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
                {counts[s] ?? 0}
              </span>
            )}
          </label>
        );
      })}
      {note != null && (
        <p className="px-1.5 pt-1 text-[10px] leading-snug text-muted-foreground">{note}</p>
      )}

      {footer}
    </div>
  );
}
